/* eslint-disable no-await-in-loop */
/**
 * Secure Upload Manager
 *
 * This upload manager uses presigned URLs from the upload service instead of
 * direct AWS SDK calls with hardcoded credentials. It maintains feature parity
 * with uploadManagerNew.js but with improved security.
 *
 * Key Features:
 * - Presigned URL uploads (no AWS credentials in client)
 * - Concurrent upload queue (max 3 active uploads)
 * - Pause/resume with state persistence
 * - Network monitoring and auto-resume
 * - Progress smoothing and ETA calculation
 * - WebSocket server-side progress tracking
 * - Retry logic with exponential backoff
 */

import { toast } from 'react-toastify';
import { UPLOAD_CONFIG } from '../Config/uploadConfig';
import { logAppEvent } from '../Api/errorLogger';
import {
  createUploadSession,
  completeUploadSession,
  uploadPartWithPresignedUrl,
  registerDocument,
  refreshPresignedUrls,
  createGiftUploadSession,
  completeGiftUploadSession,
} from '../Api/uploaderApiFunctions';
import { getUserUuid } from '../Api/authHelpers';

// Upload states (same as uploadManagerNew.js)
export const UPLOAD_STATUS = {
  PENDING: 'pending',
  INITIALIZING: 'initializing',
  UPLOADING: 'uploading',
  PAUSED: 'paused',
  FINISHING: 'finishing',
  COMPLETED: 'completed',
  S3_COMPLETE: 's3_complete', // Gift flow: S3 upload done, awaiting payment for registration
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  CANCELLING: 'cancelling',
  NETWORK_ERROR: 'network_error',
};

/**
 * Helper function to create dummy object for display during upload
 */
const createDummy = (options) => {
  const {
    draft_id,
    title,
    fileName,
    thumbnail,
    ForNoImage,
    category,
    description,
    videoSize,
    selectedGroups,
    changeSize,
  } = options;

  const cleanThumbnail = thumbnail
    ? thumbnail.replace(/,+$/, '').trim()
    : ForNoImage;

  return {
    draft_id,
    title: title || fileName,
    thumbnail: cleanThumbnail,
    category,
    video_description: description,
    video_size: changeSize ? changeSize(videoSize) : videoSize,
    type: 0,
    group_id: selectedGroups,
    content_status: 'Video',
    uploadIsTrying: 1,
  };
};

/**
 * Generate S3 key matching mobile app format
 *
 * Uses User UUID from api_response.uuid (preferred) or falls back to userId.
 * Mobile app uses UUID for S3 paths: wasabi_v2_mobile.dart:47-50
 *
 * @param {string} userId - User ID (fallback)
 * @param {string} filename - Original filename
 * @returns {string} S3 key: cloud/production/{uuid}/{timestamp}_{filename}
 */
function generateSecureS3Key(userId, filename) {
  const timestamp = Date.now();
  const cleanFilename = filename.replace(/[^a-zA-Z0-9._-]/g, '_');

  // Determine environment
  const env = window.location.origin.includes('preprod')
    ? 'pre-production'
    : 'production';

  // Prefer UUID over userId (matches mobile app)
  const userUuid = getUserUuid();
  const userIdentifier = userUuid || userId;

  if (!userUuid) {
    console.warn('[SecureUpload] User UUID not found, using userId as fallback');
  }

  return `cloud/${env}/${userIdentifier}/${timestamp}_${cleanFilename}`;
}

/**
 * Enhanced Upload Manager with Presigned URLs
 */
class UploadManagerSecure {
  constructor() {
    this.uploads = new Map(); // uploadId -> UploadTask
    this.listeners = new Set();
    this.networkStatus = 'online';
    this.networkCheckInterval = null;
    this.consecutiveNetworkFailures = 0;
    this.startNetworkMonitoring();
  }

  /**
   * Get count of active uploads
   */
  getActiveUploadCount() {
    let count = 0;
    this.uploads.forEach((task) => {
      if (
        task.status === UPLOAD_STATUS.UPLOADING
        || task.status === UPLOAD_STATUS.INITIALIZING
        || task.status === UPLOAD_STATUS.FINISHING
      ) {
        count += 1;
      }
    });
    return count;
  }

  /**
   * Get total pending items count
   */
  getPendingItemsCount() {
    let totalPending = 0;

    this.uploads.forEach((task) => {
      const isActive = task.status === UPLOAD_STATUS.PENDING
        || task.status === UPLOAD_STATUS.INITIALIZING
        || task.status === UPLOAD_STATUS.UPLOADING
        || task.status === UPLOAD_STATUS.PAUSED
        || task.status === UPLOAD_STATUS.NETWORK_ERROR
        || task.status === UPLOAD_STATUS.FINISHING;

      if (isActive) {
        if (task.uploadType === 'photo') {
          const remaining = (task.totalPhotos || 0) - (task.uploadedPhotos || 0);
          totalPending += remaining;
        } else {
          totalPending += 1;
        }
      }
    });

    return totalPending;
  }

  /**
   * Get currently uploading items count
   */
  getCurrentlyUploadingCount() {
    let totalUploading = 0;

    this.uploads.forEach((task) => {
      const isActive = task.status === UPLOAD_STATUS.PENDING
        || task.status === UPLOAD_STATUS.UPLOADING
        || task.status === UPLOAD_STATUS.INITIALIZING
        || task.status === UPLOAD_STATUS.FINISHING
        || task.status === UPLOAD_STATUS.PAUSED
        || task.status === UPLOAD_STATUS.NETWORK_ERROR;

      if (isActive) {
        if (task.uploadType === 'photo') {
          const remaining = (task.totalPhotos || 0) - (task.uploadedPhotos || 0);
          totalUploading += remaining;
        } else {
          totalUploading += 1;
        }
      }
    });

    return totalUploading;
  }

  /**
   * Start next upload in queue if slot available
   */
  startNextInQueue() {
    const activeCount = this.getActiveUploadCount();

    if (activeCount >= UPLOAD_CONFIG.MAX_CONCURRENT_UPLOADS) {
      return;
    }

    let nextUpload = null;
    this.uploads.forEach((task) => {
      if (!nextUpload && task.status === UPLOAD_STATUS.PENDING) {
        nextUpload = task;
      }
    });

    if (nextUpload) {
      console.log(`[SecureUpload] Starting queued upload: ${nextUpload.fileName}`);
      this.startUpload(nextUpload.id);
    }
  }

  /**
   * Start monitoring network status
   */
  startNetworkMonitoring() {
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));

    this.networkCheckInterval = setInterval(() => {
      this.checkNetworkStatus();
    }, UPLOAD_CONFIG.NETWORK_CHECK_INTERVAL);
  }

  handleOnline() {
    console.log('[SecureUpload] Network: ONLINE');
    this.networkStatus = 'online';
    this.consecutiveNetworkFailures = 0;
    this.notifyListeners();

    // Auto-resume paused uploads due to network
    this.uploads.forEach((task) => {
      if (task.status === UPLOAD_STATUS.NETWORK_ERROR) {
        console.log(`[SecureUpload] Auto-resuming upload ${task.id} after network restore`);
        if (!task.sessionId && task.currentPartNumber === 0) {
          const updatedTask = {
            ...task,
            status: UPLOAD_STATUS.PENDING,
            isPaused: false,
          };
          this.uploads.set(task.id, updatedTask);
          this.notifyListeners();
          this.startNextInQueue();
        } else {
          this.resumeUpload(task.id);
        }
      }
    });
  }

  handleOffline() {
    console.log('[SecureUpload] Network: OFFLINE');
    this.networkStatus = 'offline';
    this.notifyListeners();

    // Auto-pause all active uploads
    this.uploads.forEach((task) => {
      if (task.status === UPLOAD_STATUS.UPLOADING || task.status === UPLOAD_STATUS.PENDING) {
        console.log(`[SecureUpload] Auto-pausing upload ${task.id} due to network loss`);
        const updatedTask = {
          ...task,
          status: UPLOAD_STATUS.NETWORK_ERROR,
          isPaused: true,
        };
        this.uploads.set(task.id, updatedTask);
        this.notifyListeners();
      }
    });
  }

  async checkNetworkStatus() {
    if (!navigator.onLine) {
      if (this.networkStatus === 'online') {
        this.handleOffline();
      }
      return;
    }

    if (this.networkStatus === 'offline' && navigator.onLine) {
      this.handleOnline();
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      await fetch(UPLOAD_CONFIG.NETWORK_CHECK_URL, {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      this.consecutiveNetworkFailures = 0;
      if (this.networkStatus === 'offline') {
        this.handleOnline();
      }
    } catch (error) {
      this.consecutiveNetworkFailures += 1;
      if (this.consecutiveNetworkFailures >= 3 && !navigator.onLine) {
        if (this.networkStatus === 'online') {
          this.handleOffline();
        }
      }
    }
  }

  /**
   * Subscribe to upload updates
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of state changes
   */
  notifyListeners() {
    const uploadList = Array.from(this.uploads.values()).map((task) => ({
      id: task.id,
      draftId: task.draftId,
      uploadType: task.uploadType || 'video',
      fileName: task.fileName,
      file: task.file,
      status: task.status,
      progress: task.progress,
      bytesUploaded: task.bytesUploaded,
      totalBytes: task.totalBytes,
      uploadedPhotos: task.uploadedPhotos,
      totalPhotos: task.totalPhotos,
      speed: task.speed,
      timeRemaining: task.timeRemaining,
      error: task.error,
      isPaused: task.isPaused,
      metadata: task.metadata,
      s3Key: task.s3Key,
      fileUrl: task.fileUrl,
      thumbnails: task.thumbnails,
      giftUuid: task.giftUuid,
    }));

    this.listeners.forEach((callback) => {
      callback({
        uploads: uploadList,
        networkStatus: this.networkStatus,
      });
    });
  }

  /**
   * Create a photo album upload task (external upload, just tracking)
   */
  createPhotoUpload(options) {
    const {
      albumId,
      albumTitle,
      photoCount,
      updateUploadPercentages,
    } = options;

    const uploadId = albumId;

    const task = {
      id: uploadId,
      draftId: albumId,
      uploadType: 'photo',
      albumTitle,
      fileName: albumTitle || 'Photo Album',
      totalPhotos: photoCount,
      uploadedPhotos: 0,
      progress: 0,
      speed: 0,
      timeRemaining: 0,
      status: UPLOAD_STATUS.UPLOADING,
      isPaused: false,
      error: null,
      updateUploadPercentages,
      startTime: Date.now(),
      lastUpdateTime: Date.now(),
    };

    this.uploads.set(uploadId, task);

    if (updateUploadPercentages) {
      updateUploadPercentages(albumId, `0/${photoCount}`);
    }

    this.notifyListeners();

    return uploadId;
  }

  /**
   * Update photo upload progress
   */
  updatePhotoUploadProgress(uploadId, uploadedCount, totalCount) {
    const task = this.uploads.get(uploadId);
    if (!task || task.uploadType !== 'photo') return;

    const updatedTask = {
      ...task,
      uploadedPhotos: uploadedCount,
      totalPhotos: totalCount,
      progress: Math.round((uploadedCount / totalCount) * 100),
      lastUpdateTime: Date.now(),
    };

    this.uploads.set(uploadId, updatedTask);

    if (updatedTask.updateUploadPercentages) {
      updatedTask.updateUploadPercentages(uploadId, `${uploadedCount}/${totalCount}`);
    }

    this.notifyListeners();
  }

  /**
   * Complete photo upload
   */
  completePhotoUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task || task.uploadType !== 'photo') return;

    const updatedTask = {
      ...task,
      status: UPLOAD_STATUS.COMPLETED,
      progress: 100,
    };

    this.uploads.set(uploadId, updatedTask);

    if (updatedTask.updateUploadPercentages) {
      updatedTask.updateUploadPercentages(uploadId, null);
    }

    this.notifyListeners();

    if (UPLOAD_CONFIG.AUTO_HIDE_COMPLETED) {
      setTimeout(() => {
        this.removeUpload(uploadId);
      }, UPLOAD_CONFIG.AUTO_HIDE_DELAY);
    }
  }

  /**
   * Fail photo upload
   */
  failPhotoUpload(uploadId, errorMessage) {
    const task = this.uploads.get(uploadId);
    if (!task || task.uploadType !== 'photo') return;

    task.status = UPLOAD_STATUS.FAILED;
    task.error = errorMessage || 'Upload failed';

    if (task.updateUploadPercentages) {
      task.updateUploadPercentages(uploadId, null);
    }

    this.notifyListeners();
  }

  /**
   * Create and start a new upload
   */
  async createUpload(options) {
    const {
      userId,
      file,
      draftId,
      key, // Cloud uploads pass this (old S3 key format)
      metadata,
      thumbnails,
      onProgress,
      onSuccess,
      onError,
      updateUploadPercentages,
      updateUploadDetails,
      setUploadContainer,
      setUploadContainer2,
      updateDummy,
      dummyData,
      suppressToast,
      giftUuid, // Gift flow: use gift APIs (no auth token), skip registerDocument/onSuccess
    } = options;

    const uploadId = draftId;

    // Check if we're at the concurrent upload limit
    const activeCount = this.getActiveUploadCount();
    const shouldQueue = activeCount >= UPLOAD_CONFIG.MAX_CONCURRENT_UPLOADS;

    const task = {
      id: uploadId,
      draftId,
      uploadType: 'video',
      userId,
      file,
      fileName: file.name, // Always use original file name for display
      s3KeyFromCaller: key, // Store passed key for cloud uploads if needed
      thumbnails, // Store for later upload
      totalBytes: file.size,
      bytesUploaded: 0,
      progress: 0,
      speed: 0,
      smoothedSpeed: 0,
      timeRemaining: 0,
      status: shouldQueue ? UPLOAD_STATUS.PENDING : UPLOAD_STATUS.INITIALIZING,
      isPaused: false,
      error: null,
      metadata,
      onProgress,
      onSuccess,
      onError,
      updateUploadPercentages,
      updateUploadDetails,
      setUploadContainer,
      setUploadContainer2,
      updateDummy,
      dummyData,
      // Upload state
      sessionId: null,
      s3Key: null,
      thumbnailKeys: [],
      presignedUrls: [],
      parts: [],
      currentPartNumber: 0,
      startTime: Date.now(),
      lastUpdateTime: Date.now(),
      lastSpeedUpdateTime: Date.now(),
      lastBytesUploaded: 0,
      retryCount: 0,
      uploadMode: 'presigned-url', // Identifier for resume logic
      suppressToast: !!suppressToast, // Gift flow has its own progress UI
      giftUuid: giftUuid || null, // Gift flow: UUID-based auth, no token needed
    };

    this.uploads.set(uploadId, task);

    // Add to upload containers
    if (setUploadContainer) {
      setUploadContainer((prev) => [...prev, draftId]);
    }
    if (setUploadContainer2) {
      setUploadContainer2((prev) => [...prev, draftId]);
    }

    // Create dummy if provided
    if (dummyData && updateDummy) {
      const dummy = createDummy({
        ...dummyData,
        draft_id: draftId,
      });
      updateDummy(dummy, 1);
    }

    // Show toast notification (suppressed for gift flow which has its own progress UI)
    if (!task.suppressToast) {
      const trimmedFilename = file.name.length > 26 ? `${file.name.substring(0, 26)}...` : file.name;
      toast(trimmedFilename, {
        autoClose: false,
        toastId: draftId,
        position: toast.POSITION.BOTTOM_RIGHT,
        closeButton: false,
      });
    }

    // Update status
    if (updateUploadPercentages) {
      if (shouldQueue) {
        updateUploadPercentages(draftId, 'In queue...');
      } else {
        updateUploadPercentages(draftId, 'Initializing');
      }
    }

    this.notifyListeners();

    // Start upload immediately if not queued
    if (!shouldQueue) {
      this.startUpload(uploadId);
    }

    return uploadId;
  }

  /**
   * Start or resume upload
   */
  async startUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task) return;

    try {
      // Check network
      if (this.networkStatus === 'offline') {
        task.status = UPLOAD_STATUS.NETWORK_ERROR;
        task.isPaused = true;
        this.notifyListeners();
        return;
      }

      task.status = UPLOAD_STATUS.UPLOADING;
      task.isPaused = false;

      if (!task.startTime || task.startTime === 0) {
        task.startTime = Date.now();
      }

      task.lastUpdateTime = Date.now();
      task.lastSpeedUpdateTime = Date.now();
      task.lastBytesUploaded = task.bytesUploaded;
      task.smoothedSpeed = 0;

      this.notifyListeners();

      // STEP 1: Skip separate thumbnail upload - thumbnails will be sent as binary in API call
      // This matches the old flow and is simpler (one less upload phase)
      console.log('[SecureUpload] Thumbnails will be sent as binary data in final API call');

      // STEP 2: Create upload session (if not already done)
      if (!task.sessionId) {
        let session;

        if (task.giftUuid) {
          // Gift flow: use gift API with UUID, server generates S3 key
          console.log(`[SecureUpload] Creating GIFT upload session for ${task.fileName} (uuid: ${task.giftUuid})`);

          session = await createGiftUploadSession(task.giftUuid, {
            file_name: task.fileName,
            file_size: task.file.size,
            content_type: task.file.type || 'video/mp4',
          });

          // Server returns the S3 key
          task.s3Key = session.s3_key || session.file_key || null;
        } else {
          // Normal flow: generate S3 key locally, use token-based API
          task.s3Key = generateSecureS3Key(task.userId, task.fileName);

          console.log(`[SecureUpload] Creating upload session for ${task.fileName}`);
          console.log(`[SecureUpload] S3 Key: ${task.s3Key}`);

          session = await createUploadSession({
            file_name: task.fileName,
            file_size: task.file.size,
            content_type: task.file.type || 'video/mp4',
            s3_key: task.s3Key,
          });
        }

        task.sessionId = session.id;
        task.presignedUrls = session.presigned_urls;
        task.partSize = session.part_size;
        task.totalParts = session.total_parts;

        console.log(`[SecureUpload] Session created: ${task.sessionId}, ${task.totalParts} parts`);
      }

      // Update status
      if (task.updateUploadPercentages) {
        task.updateUploadPercentages(task.draftId, 'Starting Upload');
      }

      // STEP 3: Upload parts using presigned URLs
      for (
        let partNumber = task.currentPartNumber + 1;
        partNumber <= task.totalParts;
        partNumber += 1
      ) {
        // Check if paused or cancelled
        if (task.isPaused || task.status === UPLOAD_STATUS.CANCELLED || task.status === UPLOAD_STATUS.CANCELLING) {
          task.currentPartNumber = partNumber - 1;
          return;
        }

        // Check network
        if (this.networkStatus === 'offline') {
          task.status = UPLOAD_STATUS.NETWORK_ERROR;
          task.isPaused = true;
          task.currentPartNumber = partNumber - 1;
          this.notifyListeners();
          return;
        }

        const start = (partNumber - 1) * task.partSize;
        const end = Math.min(start + task.partSize, task.file.size);
        const partBlob = task.file.slice(start, end);

        // Get presigned URL for this part
        const presignedUrlObj = task.presignedUrls.find(
          (url) => url.part_number === partNumber || url.partNumber === partNumber
        );

        if (!presignedUrlObj) {
          console.error(`[SecureUpload] No presigned URL for part ${partNumber}`);
          throw new Error(`Missing presigned URL for part ${partNumber}`);
        }

        const presignedUrl = presignedUrlObj.presigned_url || presignedUrlObj.url;

        // Upload part with retry
        let uploadSuccess = false;
        let retryCount = 0;

        while (!uploadSuccess && retryCount < UPLOAD_CONFIG.RETRY_ATTEMPTS) {
          try {
            const bytesBeforePart = start;

            // Create XHR ref so cancelUpload can abort the active request
            const xhrRef = { current: null };
            task.activeXhrRef = xhrRef;

            const part = await uploadPartWithPresignedUrl(
              presignedUrl,
              partBlob,
              partNumber,
              (loaded, total) => {
                const currentPartUploaded = loaded || 0;
                let totalUploaded = bytesBeforePart + currentPartUploaded;
                totalUploaded = Math.max(totalUploaded, task.bytesUploaded);

                task.bytesUploaded = totalUploaded;
                task.progress = Math.min(
                  99,
                  Math.round((totalUploaded / task.totalBytes) * 100)
                );

                const currentTime = Date.now();
                const timeDiffProgress = (currentTime - task.lastUpdateTime) / 1000;
                const timeDiffSpeed = (currentTime - task.lastSpeedUpdateTime) / 1000;

                const shouldUpdateProgress = timeDiffProgress >= UPLOAD_CONFIG.PROGRESS_UPDATE_INTERVAL / 1000;
                const shouldUpdateSpeed = timeDiffSpeed >= UPLOAD_CONFIG.SPEED_UPDATE_INTERVAL / 1000;

                if (shouldUpdateSpeed) {
                  const bytesDiff = totalUploaded - task.lastBytesUploaded;

                  if (bytesDiff > 0) {
                    const instantSpeed = bytesDiff / timeDiffSpeed;
                    const alpha = 0.3;
                    task.smoothedSpeed = task.smoothedSpeed === 0
                      ? instantSpeed
                      : alpha * instantSpeed + (1 - alpha) * task.smoothedSpeed;

                    task.speed = task.smoothedSpeed;
                    task.timeRemaining = (task.totalBytes - totalUploaded) / task.speed;
                  }

                  task.lastSpeedUpdateTime = currentTime;
                  task.lastBytesUploaded = totalUploaded;
                }

                if (shouldUpdateProgress) {
                  task.lastUpdateTime = currentTime;

                  if (task.updateUploadPercentages) {
                    task.updateUploadPercentages(task.draftId, task.progress);
                  }

                  if (!task.suppressToast) {
                    const trimmedFilename = task.fileName.length > 26
                      ? `${task.fileName.substring(0, 26)}...`
                      : task.fileName;
                    toast.update(task.draftId, {
                      render: `${trimmedFilename} ${task.progress}%`,
                      autoClose: false,
                    });
                  }

                  if (task.onProgress) {
                    task.onProgress(task.progress, totalUploaded, task.totalBytes);
                  }

                  this.notifyListeners();
                }

                if (shouldUpdateSpeed && task.updateUploadDetails) {
                  const elapsedSize = totalUploaded / 1024 / 1024;
                  const elapsedTime = (currentTime - task.startTime) / 1000;
                  const remainingSize = (task.totalBytes - totalUploaded) / 1024 / 1024;

                  task.updateUploadDetails(task.draftId, {
                    elapsed_size: elapsedSize,
                    elapsed_time: elapsedTime,
                    remaining_size: remainingSize > 0 ? remainingSize : 0,
                    speed: task.speed,
                    time_remaining: task.timeRemaining,
                  });
                }
              },
              xhrRef
            );

            task.activeXhrRef = null;
            task.parts.push(part);
            task.currentPartNumber = partNumber;
            this.notifyListeners();
            uploadSuccess = true;
          } catch (error) {
            retryCount += 1;
            console.error(`[SecureUpload] Error uploading part ${partNumber}, retry ${retryCount}:`, error);

            if (retryCount >= UPLOAD_CONFIG.RETRY_ATTEMPTS) {
              throw error;
            }

            await new Promise((resolve) => setTimeout(resolve, UPLOAD_CONFIG.RETRY_DELAY));
          }
        }
      }

      // STEP 4: Complete multipart upload
      const sortedParts = [...task.parts].sort((a, b) => a.PartNumber - b.PartNumber);

      console.log(`[SecureUpload] Completing upload session for ${task.fileName}`);

      if (task.giftUuid) {
        const completeResponse = await completeGiftUploadSession(task.sessionId, task.giftUuid, sortedParts);
        // Server may return file_url or s3_key in completion response
        if (completeResponse?.file_url) {
          task.fileUrl = completeResponse.file_url;
        }
        if (completeResponse?.s3_key || completeResponse?.file_key) {
          task.s3Key = completeResponse.s3_key || completeResponse.file_key;
        }
      } else {
        await completeUploadSession(task.sessionId, sortedParts);
      }

      // Gift flow: S3 upload done — skip registerDocument and onSuccess (need token after payment)
      if (task.giftUuid) {
        task.status = UPLOAD_STATUS.S3_COMPLETE;
        task.progress = 100;
        this.notifyListeners();

        console.log(`[SecureUpload] Gift upload S3 complete for ${task.fileName}, awaiting payment for registration`);

        if (task.updateUploadPercentages) {
          task.updateUploadPercentages(task.draftId, 'Uploaded');
        }

        if (task.onProgress) {
          task.onProgress(100, task.totalBytes, task.totalBytes);
        }

        // Start next upload in queue
        this.startNextInQueue();
        return;
      }

      // STEP 5: Register document (normal flow only — not gift)
      task.status = UPLOAD_STATUS.FINISHING;
      task.progress = 100;
      this.notifyListeners();

      if (task.updateUploadPercentages) {
        task.updateUploadPercentages(task.draftId, 'Please wait');
      }

      if (!task.suppressToast) {
        toast.update(task.draftId, {
          render: 'Finalizing upload...',
          type: toast.TYPE.INFO,
          autoClose: false,
          closeButton: false,
        });
      }

      console.log(`[SecureUpload] Registering document for ${task.fileName}`);

      // Use video URL as thumbnail (thumbnails will be sent as binary in addVideo API)
      const documentResponse = await registerDocument({
        name: task.fileName,
        url: task.s3Key,
        thumbnail: task.s3Key, // Use video URL as thumbnail placeholder
        thumbnails: [], // Empty array - thumbnails sent as binary in addVideo API
        uploaded_at: new Date().toISOString(),
      });

      task.documentId = documentResponse.id;

      console.log(`[SecureUpload] Document registered: ${task.documentId}`);

      // STEP 6: Call success callback (adds video to backend + links draft)
      if (task.onSuccess) {
        await task.onSuccess(task.draftId, {
          s3Key: task.s3Key,
          thumbnails: task.thumbnails, // Pass binary thumbnails to API call
          documentId: task.documentId,
        });
      }

      // Upload complete
      task.status = UPLOAD_STATUS.COMPLETED;
      this.notifyListeners();

      if (!task.suppressToast) {
        toast.update(task.draftId, {
          render: 'Upload completed!',
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
          closeButton: false,
        });
      }

      // Remove from upload containers
      if (task.setUploadContainer) {
        task.setUploadContainer((prev) => prev.filter((id) => id !== task.draftId));
      }
      if (task.setUploadContainer2) {
        task.setUploadContainer2((prev) => prev.filter((id) => id !== task.draftId));
      }

      // Auto-remove completed upload after delay
      if (UPLOAD_CONFIG.AUTO_HIDE_COMPLETED) {
        setTimeout(() => {
          this.removeUpload(uploadId);
        }, UPLOAD_CONFIG.AUTO_HIDE_DELAY);
      }

      // Start next upload in queue
      this.startNextInQueue();
    } catch (error) {
      // Don't overwrite CANCELLED/CANCELLING status with FAILED
      if (task.status === UPLOAD_STATUS.CANCELLED || task.status === UPLOAD_STATUS.CANCELLING) {
        console.log('[SecureUpload] Upload was cancelled, ignoring error:', error.message);
        return;
      }

      console.error('[SecureUpload] Upload error:', error);

      logAppEvent({
        userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: 'Add Video',
        description: `Video upload failed — file: "${task.fileName || 'unknown'}", progress: ${task.progress || 0}%, reason: ${error.message || 'Unknown error'}`,
        severity: 'high',
        errorMessage: error.message || 'Upload pipeline failed',
        errorStack: error.stack || null,
        component: 'uploadManagerSecure',
        userAction: `uploading video: ${task.fileName || ''}`,
      });

      task.status = UPLOAD_STATUS.FAILED;
      task.error = error.message || 'Upload failed';

      if (task.updateUploadPercentages) {
        task.updateUploadPercentages(task.draftId, 'Failed');
      }

      if (!task.suppressToast) {
        toast.update(task.draftId, {
          render: 'Upload failed',
          type: toast.TYPE.ERROR,
          autoClose: 3000,
          closeButton: false,
        });
      }

      // Remove from upload containers
      if (task.setUploadContainer) {
        task.setUploadContainer((prev) => prev.filter((id) => id !== task.draftId));
      }
      if (task.setUploadContainer2) {
        task.setUploadContainer2((prev) => prev.filter((id) => id !== task.draftId));
      }

      if (task.onError) {
        task.onError(error, task.draftId);
      }

      this.notifyListeners();

      // Start next upload in queue
      this.startNextInQueue();
    }
  }

  /**
   * Pause upload
   */
  pauseUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task) return;

    if (task.status === UPLOAD_STATUS.UPLOADING) {
      task.status = UPLOAD_STATUS.PAUSED;
      task.isPaused = true;
      this.notifyListeners();

      // Start next upload in queue
      this.startNextInQueue();
    }
  }

  /**
   * Resume upload
   */
  resumeUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task) return;

    if (
      task.status === UPLOAD_STATUS.PAUSED
      || task.status === UPLOAD_STATUS.NETWORK_ERROR
    ) {
      if (this.networkStatus === 'offline') {
        if (!task.suppressToast) {
          toast.error('Cannot resume: No network connection');
        }
        return;
      }

      task.status = UPLOAD_STATUS.UPLOADING;
      task.isPaused = false;
      this.startUpload(uploadId);
    }
  }

  /**
   * Cancel upload
   */
  async cancelUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task) return;

    task.status = UPLOAD_STATUS.CANCELLING;
    task.isPaused = true;
    this.notifyListeners();

    // Abort the active XHR request if one is in progress
    if (task.activeXhrRef?.current) {
      try {
        task.activeXhrRef.current.abort();
      } catch (e) {
        console.warn('[SecureUpload] Error aborting XHR:', e);
      }
      task.activeXhrRef = null;
    }

    task.status = UPLOAD_STATUS.CANCELLED;

    if (task.updateUploadPercentages) {
      task.updateUploadPercentages(task.draftId, 'Cancelled');
    }

    if (!task.suppressToast) {
      toast.update(task.draftId, {
        render: 'Upload cancelled',
        type: toast.TYPE.ERROR,
        autoClose: 3000,
        closeButton: false,
      });
    }

    // Remove from upload containers
    if (task.setUploadContainer) {
      task.setUploadContainer((prev) => prev.filter((id) => id !== task.draftId));
    }
    if (task.setUploadContainer2) {
      task.setUploadContainer2((prev) => prev.filter((id) => id !== task.draftId));
    }

    if (task.updateDummy) {
      task.updateDummy(task.draftId, 0);
    }

    this.notifyListeners();

    setTimeout(() => {
      this.uploads.delete(uploadId);
      this.notifyListeners();
      this.startNextInQueue();
    }, 500);
  }

  /**
   * Remove upload from manager
   */
  removeUpload(uploadId) {
    this.uploads.delete(uploadId);
    this.notifyListeners();
  }

  /**
   * Get all uploads
   */
  getAllUploads() {
    return Array.from(this.uploads.values()).map((task) => ({
      id: task.id,
      draftId: task.draftId,
      fileName: task.fileName,
      status: task.status,
      progress: task.progress,
      bytesUploaded: task.bytesUploaded,
      totalBytes: task.totalBytes,
      speed: task.speed,
      timeRemaining: task.timeRemaining,
      error: task.error,
      isPaused: task.isPaused,
      metadata: task.metadata,
    }));
  }

  /**
   * Get upload by ID
   */
  getUpload(uploadId) {
    return this.uploads.get(uploadId);
  }

  /**
   * Get all gift uploads that finished S3 upload but need registration after payment
   */
  getUploadStatus(uploadId) {
    const task = this.uploads.get(uploadId);
    return task ? task.status : null;
  }

  getS3CompleteUploads() {
    const results = [];
    this.uploads.forEach((task) => {
      if (task.status === UPLOAD_STATUS.S3_COMPLETE && task.giftUuid) {
        results.push({
          id: task.id,
          draftId: task.draftId,
          fileName: task.fileName,
          s3Key: task.s3Key,
          fileUrl: task.fileUrl,
          thumbnails: task.thumbnails,
          file: task.file,
        });
      }
    });
    return results;
  }

  /**
   * Mark a gift upload as fully completed after post-payment registration
   */
  markUploadCompleted(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task) return;

    task.status = UPLOAD_STATUS.COMPLETED;
    this.notifyListeners();

    // Remove from upload containers
    if (task.setUploadContainer) {
      task.setUploadContainer((prev) => prev.filter((id) => id !== task.draftId));
    }
    if (task.setUploadContainer2) {
      task.setUploadContainer2((prev) => prev.filter((id) => id !== task.draftId));
    }

    if (UPLOAD_CONFIG.AUTO_HIDE_COMPLETED) {
      setTimeout(() => {
        this.removeUpload(uploadId);
      }, UPLOAD_CONFIG.AUTO_HIDE_DELAY);
    }
  }

  /**
   * Cleanup
   */
  destroy() {
    clearInterval(this.networkCheckInterval);
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));
    this.uploads.clear();
    this.listeners.clear();
  }
}

// Create singleton instance
const uploadManagerSecure = new UploadManagerSecure();

export default uploadManagerSecure;

/**
 * Wrapper function for compatibility with existing code
 */
export async function uploadLargeFileSecure(options) {
  return uploadManagerSecure.createUpload(options);
}
